<script setup lang="ts">
import { TotemTeleportDestination } from '@totem/vue';
import { useHead } from '@unhead/vue';

import useMarque from '@/plugins/layout/lib/UseMarque';

const { marqueName } = useMarque();

useHead({
  title: import.meta.env.VITE_APP_NAME,
});
</script>

<template>
  <div id="app" :data-theme="marqueName">
    <RouterView />

    <TotemTeleportDestination />
  </div>
</template>

<style lang="scss">
@use 'sass:map';
@use '@alptis/eda-vue-component' as evc;
@use '@totem/scss' as totem;
@use '@totem/scss/themes/alptis.scss' as alptis-theme;
@use '@totem/scss/components.scss';

@use './core/assets/scss/fonts.scss';
@use '@alptis/eda-vue-component/app-vite.scss';
@use '@totem/scss/evc-overrides.scss';

#app {
  height: 100vh;
  position: relative;
}

:root {
  @include totem.init-theme-variables(alptis-theme.$variables);

  --transition-duration-default: 150ms;
  --disabled-opacity: 0.3;

  --header-height: 72px;
  --breadcrumb-height: 23px;
  --nav-bar-width: 110px;
  --nav-menu-width: 246px;
}

@include evc.themed using($theme) {
  // Branding
  --branding-hospitalisation: #{evc.get($theme, branding-hospitalisation)};
  --branding-soins-courant: #{evc.get($theme, branding-soins-courant)};
  --branding-frais-de-sante: #{evc.get($theme, branding-frais-de-sante)};
  --branding-surcomplementaire: #{evc.get($theme, branding-surcomplementaire)};
  --branding-sante: #{evc.get($theme, branding-sante)};
  --branding-sante-alpha-15: #{rgba(evc.get($theme, branding-sante), 0.15)};
  --branding-sante-light: #{evc.get($theme, branding-sante-light)};
  --branding-sante-dark: #{evc.get($theme, branding-sante-dark)};
  --branding-sante-darker: #{evc.get($theme, branding-sante-darker)};
  --branding-prevoyance: #{evc.get($theme, branding-prevoyance)};
  --branding-prevoyance-light: #{evc.get($theme, branding-prevoyance-light)};
  --branding-prevoyance-dark: #{evc.get($theme, branding-prevoyance-dark)};
  --branding-prevoyance-darker: #{evc.get($theme, branding-prevoyance-darker)};
  --branding-emprunteur: #{evc.get($theme, branding-emprunteur)};
  --branding-emprunteur-alpha-15: #{rgba(evc.get($theme, branding-emprunteur), 0.15)};
  --branding-emprunteur-light: #{evc.get($theme, branding-emprunteur-light)};
  --branding-emprunteur-dark: #{evc.get($theme, branding-emprunteur-dark)};
  --branding-emprunteur-darker: #{evc.get($theme, branding-emprunteur-darker)};
  --branding-collectif: #{evc.get($theme, branding-collectif)};
  --branding-collectif-light: #{evc.get($theme, branding-collectif-light)};
  --branding-collectif-dark: #{evc.get($theme, branding-collectif-dark)};
  --branding-collectif-darker: #{evc.get($theme, branding-collectif-darker)};
  --branding-epargne-retraite: #{evc.get($theme, branding-epargne-retraite)};
  --branding-epargne-retraite-light: #{evc.get($theme, branding-epargne-retraite-light)};
  --branding-epargne-retraite-dark: #{evc.get($theme, branding-epargne-retraite-dark)};
  --branding-epargne-retraite-darker: #{evc.get($theme, branding-epargne-retraite-darker)};
  --branding-financement: #{evc.get($theme, branding-financement)};
  --branding-financement-light: #{evc.get($theme, branding-financement-light)};
  --branding-financement-dark: #{evc.get($theme, branding-financement-dark)};
  --branding-financement-darker: #{evc.get($theme, branding-financement-darker)};

  // Neutral
  --neutral-black: #{evc.get($theme, neutral-black)};
  --neutral-black-alpha-15: #{rgba(evc.get($theme, neutral-black), 0.15)};
  --neutral-dark: #{evc.get($theme, neutral-dark)};
  --neutral-dark-alpha-10: #{rgba(evc.get($theme, neutral-dark), 0.1)};
  --neutral-dark-alpha-20: #{rgba(evc.get($theme, neutral-dark), 0.2)};
  --neutral-dark-alpha-30: #{rgba(evc.get($theme, neutral-dark), 0.3)};
  --neutral-medium: #{evc.get($theme, neutral-medium)};
  --neutral-light: #{evc.get($theme, neutral-light)};
  --neutral-lighter: #{evc.get($theme, neutral-lighter)};
  --neutral-lighter-alpha-30: #{rgba(evc.get($theme, neutral-lighter), 0.3)};
  --neutral-white: #{evc.get($theme, neutral-white)};

  // Semantics
  --semantic-in-progress-dark: #{evc.get($theme, semantic-in-progress-dark)};
  --semantic-in-progress: #{evc.get($theme, semantic-in-progress)};
  --semantic-in-progress-light: #{evc.get($theme, semantic-in-progress-light)};
  --semantic-in-progress-lighter: #{evc.get($theme, semantic-in-progress-lighter)};
  --semantic-warning-dark: #{evc.get($theme, semantic-warning-dark)};
  --semantic-warning: #{evc.get($theme, semantic-warning)};
  --semantic-warning-alpha-10: #{evc.get($theme, semantic-warning10)};
  --semantic-warning-light: #{evc.get($theme, semantic-warning-light)};
  --semantic-warning-lighter: #{evc.get($theme, semantic-warning-lighter)};
  --semantic-danger-dark: #{evc.get($theme, semantic-danger-dark)};
  --semantic-danger: #{evc.get($theme, semantic-danger)};
  --semantic-danger-alpha-10: #{evc.get($theme, semantic-danger10)};
  --semantic-danger-light: #{evc.get($theme, semantic-danger-light)};
  --semantic-danger-lighter: #{evc.get($theme, semantic-danger-lighter)};
  --semantic-success-dark: #{evc.get($theme, semantic-success-dark)};
  --semantic-success: #{evc.get($theme, semantic-success)};
  --semantic-success-light: #{evc.get($theme, semantic-success-light)};
  --semantic-success-lighter: #{evc.get($theme, semantic-success-lighter)};
  --semantic-disabled: #{evc.get($theme, semantic-disabled)};

  // Fonts weights
  --font-weight-light: #{evc.get($theme, font-weight-light)};
  --font-weight-regular: #{evc.get($theme, font-weight-regular)};
  --font-weight-medium: #{evc.get($theme, font-weight-medium)};
  --font-weight-semi-bold: #{evc.get($theme, font-weight-semi-bold)};
  --font-weight-bold: #{evc.get($theme, font-weight-bold)};

  // Shadows
  --shadow-1: #{evc.get($theme, shadow-1)};
  --shadow-2: #{evc.get($theme, shadow-2)};
  --shadow-3: #{evc.get($theme, shadow-3)};
  --shadow-4: #{evc.get($theme, shadow-4)};

  // Overlay shaders
  --shader-1: #{evc.get($theme, shader-1)};

  // Sizes
  --size-2: #{evc.get($theme, size-2)};
  --size-4: #{evc.get($theme, size-4)};
  --size-5: #{evc.get($theme, size-5)};
  --size-8: #{evc.get($theme, size-8)};
  --size-10: #{evc.get($theme, size-10)};
  --size-12: #{evc.get($theme, size-12)};
  --size-13: #{evc.get($theme, size-13)};
  --size-14: #{evc.get($theme, size-14)};
  --size-16: #{evc.get($theme, size-16)};
  --size-17: #{evc.get($theme, size-17)};
  --size-18: #{evc.get($theme, size-18)};
  --size-20: #{evc.get($theme, size-20)};
  --size-22: #{evc.get($theme, size-22)};
  --size-24: #{evc.get($theme, size-24)};
  --size-26: #{evc.get($theme, size-26)};
  --size-30: #{evc.get($theme, size-30)};
  --size-40: #{evc.get($theme, size-40)};

  // Radius
  --radius-large: #{evc.get($theme, radius-large)};
  --radius-circle: #{evc.get($theme, radius-circle)};

  // z-index
  --input-picto-z-index: #{evc.get($theme, input-picto-z-index)};
  --overall: #{evc.get($theme, overall)};
  --loading: #{evc.get($theme, loading)};
  --popin: #{evc.get($theme, popin)};
  --z-index-header: #{evc.get($theme, z-index-header)};
  --overlay: #{evc.get($theme, overlay)};

  // Theme colors
  --branding-primary-dark: #{evc.get($theme, branding-primary-dark)};
  --branding-primary: #{evc.get($theme, branding-primary)};
  --branding-primary-alpha-20: #{rgba(evc.get($theme, branding-primary), 0.2)};
  --branding-primary-light: #{evc.get($theme, branding-primary-light)};
  --neutral-darker: #{evc.get($theme, neutral-darker)};

  // background
  --background-niveau: #{evc.get($theme, background-niveau)};
  --background-niveau-darker: #{evc.get($theme, background-niveau-darker)};
  --background-header-dark: #{evc.get($theme, background-header-dark)};
  --background-header: #{evc.get($theme, background-header)};
  --background-header-light: #{evc.get($theme, background-header-light)};
  --background-footer-dark: #{evc.get($theme, background-footer-dark)};
  --background-footer: #{evc.get($theme, background-footer)};
  --background-footer-light: #{evc.get($theme, background-footer-light)};
  --background-page-dark: #{evc.get($theme, background-page-dark)};
  --background-page: #{evc.get($theme, background-page)};
  --background-page-light: #{evc.get($theme, background-page-light)};
  --background-darker: #{evc.get($theme, background-darker)};
  --background-dark: #{evc.get($theme, background-dark)};
  --background-medium: #{evc.get($theme, background-medium)};
  --background-light: #{evc.get($theme, background-light)};
  --background-lighter: #{evc.get($theme, background-lighter)};
  --background-nav: #{evc.get($theme, background-nav)};
  --background-nav-dark: #{evc.get($theme, background-nav-dark)};
  --background-nav-darker: #{evc.get($theme, background-nav-darker)};
  --background-nav-darker-alpha-30: #{rgba(evc.get($theme, background-nav-darker), 0.3)};
  --background-nav-darker-alpha-50: #{rgba(evc.get($theme, background-nav-darker), 0.5)};
  --background-nav-light: #{evc.get($theme, background-nav-light)};
  --background-nav-lighter: #{evc.get($theme, background-nav-lighter)};

  // button
  --button-color: #{evc.get($theme, button-color)};
  --button-background-color: #{evc.get($theme, button-background-color)};
  --button-hover-color: #{evc.get($theme, button-hover-color)};
  --button-hover-background-color: #{evc.get($theme, button-hover-background-color)};
  --button-active-background-color: #{evc.get($theme, button-active-background-color)};
  --button-outlined-color: #{evc.get($theme, button-outlined-color)};
  --button-outlined-border-color: #{evc.get($theme, button-outlined-border-color)};
  --button-outlined-hover-background-color: #{evc.get($theme, button-outlined-hover-background-color)};
  --button-outlined-active-background-color: #{evc.get($theme, button-outlined-active-background-color)};
  --button-icon-hover-background-color: #{evc.get($theme, button-icon-hover-background-color)};
  --button-icon-hover-active-color: #{evc.get($theme, button-icon-hover-active-color)};
  --button-icon-active-background-color: #{evc.get($theme, button-icon-active-background-color)};
  --button-scrolltop: #{evc.get($theme, button-scrolltop)};
  --button-scrolltop-hover: #{evc.get($theme, button-scrolltop-hover)};
  --button-scrolltop-active: #{evc.get($theme, button-scrolltop-active)};

  // link
  --link-color: #{evc.get($theme, link-color)};
  --link-hover-color: #{evc.get($theme, link-hover-color)};
  --link-hover-background-color: #{evc.get($theme, link-hover-background-color)};
  --link-active-color: #{evc.get($theme, link-active-color)};
  --link-active-background-color: #{evc.get($theme, link-active-background-color)};

  // radio button
  --radio-button-background-hover: #{evc.get($theme, radio-button-background-hover)};

  // input
  --label-color: #{evc.get($theme, label-color)};
  --input-hover-background-color: #{evc.get($theme, input-hover-background-color)};
  --input-readonly-background-color: #{evc.get($theme, input-readonly-background-color)};
  --input-active-background-color: #{evc.get($theme, input-active-background-color)};

  // checkbox
  --checkbox-hover-background: #{evc.get($theme, checkbox-hover-background)};
  --checkbox-hover-icon-color: #{evc.get($theme, checkbox-hover-icon-color)};
  --checkbox-focus-box-shadow: #{evc.get($theme, checkbox-focus-box-shadow)};

  // radio
  --radio-hover-background: #{evc.get($theme, radio-hover-background)};
  --radio-hover-icon-color: #{evc.get($theme, radio-hover-icon-color)};
  --radio-focus-box-shadow: #{evc.get($theme, radio-focus-box-shadow)};
}

@include totem.init-base();

// Base
:root {
  --page-padding: 32px 40px;
}

html {
  overflow: hidden auto;

  &.visible-scrollbar {
    overflow-y: scroll;
  }
}

a {
  color: inherit;
  text-decoration: inherit;

  // Stylize default links (those having no CSS class)
  // Avoids to override default styles every time you need to customize a specific link
  &:not([class]),
  &[class=''] {
    @extend %totem-typography-link2;
  }
}
</style>
